import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { getNextBatchNumber, type Device } from '../services/devices';
import {
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Button,
} from './styled';

const Summary = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
`;

const CompanyBreakdown = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};

  li {
    padding: ${({ theme }) => theme.spacing(1)} 0;
  }
`;

interface DeployModalProps {
  devices: Device[];
  onConfirm: (deploymentBatch: string) => void;
  onClose: () => void;
  isPending?: boolean;
}

export default function DeployModal({ devices, onConfirm, onClose, isPending }: DeployModalProps) {
  const [batchInput, setBatchInput] = useState('');

  const { data: suggestedBatch } = useQuery({
    queryKey: ['next-batch-number'],
    queryFn: getNextBatchNumber,
  });

  const companyGroups = devices.reduce<Record<string, number>>((acc, d) => {
    const name = d.company_name || 'Unassigned';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  const effectiveBatch = batchInput || suggestedBatch || '';

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="sm" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>Deploy Devices</h2>
          <ModalCloseButton onClick={onClose}>&times;</ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <Summary>
            Deploying {devices.length} device{devices.length !== 1 ? 's' : ''}
          </Summary>
          <CompanyBreakdown>
            {Object.entries(companyGroups).map(([name, count]) => (
              <li key={name}>{name}: {count}</li>
            ))}
          </CompanyBreakdown>
          <FormGroup>
            <Label htmlFor="deployment-batch">Batch Number</Label>
            <Input
              id="deployment-batch"
              type="text"
              value={batchInput}
              onChange={(e) => setBatchInput(e.target.value)}
              placeholder={suggestedBatch || 'BATCH-001'}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => onConfirm(effectiveBatch)}
            disabled={isPending || !effectiveBatch}
          >
            {isPending ? 'Deploying...' : 'Deploy'}
          </Button>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
}
